import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

const LONG_CARDS = [
  {
    color: 'text-[#71717a]', label: '정상', condition: '복약이행률 80% 이상',
    message: '발송 안 함 (이벤트성만 가끔 발송)',
  },
  {
    color: 'text-[#f7a032]', label: '저조', condition: '복약이행률 30~79%',
    message: '최근 일부 복약 기록이 확인되지 않았습니다. 복약관리에서 복용 여부를 확인하실 수 있습니다.',
    meta: '재발송 간격 · 알림톡 3일 / 앱푸시 1일',
  },
  {
    color: 'text-[#f04351]', label: '중단위험', condition: '복약이행률 30% 이하 또는 2회 이상 미복용',
    message: '최근 처방받으신 약의 복약 기록이 확인되지 않고 있습니다. 복약관리에서 복용 여부를 확인하실 수 있습니다.',
    meta: '단독 발송 (단기 메시지 차단) · 재발송 알림톡 3일 / 앱푸시 2일', metaClass: 'font-semibold text-[#3f3f46]',
  },
  {
    color: 'text-[#58a2f9]', label: '종료임박', condition: '복약 종료 3일 이내 또는 종료',
    message: '현재 복용 중인 약이 곧 마무리될 예정입니다. 추가 복용 또는 상담이 필요하신 경우 약국으로 문의해 주세요.',
    meta: '1회만 발송 (이벤트성)',
  },
  {
    color: 'text-[#f04351]', label: '이탈', condition: '장기 미복용 + 메시지 반응 없음', pill: true,
    message: '회원님께서 등록하신 복약 일정에 대해 일정 기간 동안 복약 기록이 지속적으로 확인되지 않아 복약 기록을 확인해 주세요.',
    meta: '약사 직접 전화 필요 · 단독 발송 · 재발송 알림톡 3일 / 앱푸시 2일', metaClass: 'font-semibold text-[#f04351]',
  },
];

const SHORT_CARDS = [
  {
    color: 'text-[#007aff]', label: '복약 시작', condition: '처방 시작 당일',
    message: '처방받으신 약의 복용 방법 및 시간은 복약관리에서 확인하실 수 있습니다.', meta: '1회만 발송',
  },
  {
    color: 'text-[#007aff]', label: '복약 진행 확인', condition: '복약 기간 50% 시점',
    message: '현재 복용 중인 약의 복약 일정이 진행 중이며, 복약 기록을 확인하실 수 있습니다.', meta: '1회만 발송',
  },
  {
    color: 'text-[#f79103]', label: '종료 임박', condition: '복약 종료 1~2일 전',
    message: '회원님께서 복용 중인 약의 복약 기간이 곧 종료될 예정입니다. 복약 상태를 확인하실 수 있습니다.', meta: '1회만 발송',
  },
  {
    color: 'text-[#71717a]', label: '복약 종료', condition: '복약 종료 시점',
    message: '회원님께서 복용 중인 약의 복약 기간이 종료된 것으로 확인되어 안내드립니다.', meta: '1회만 발송',
  },
];

const POLICY_ITEMS = [
  '동일 환자 하루 최대 2건 (장기 1 + 단기 1)',
  '장기 메시지 발송 · 오전 10:00 ~ 11:00',
  '단기 메시지 발송 · 오후 15:00 ~ 16:00',
  '중단 위험 / 이탈 상태에서는 단기 메시지 자동 차단',
  '발송 채널 · 알림톡 → 친구톡 → 앱푸시 (순차 재시도)',
];

// 장기: tag/challenge(px8 py2 r16, 이탈은 pill) · 단기: 라벨 텍스트만 — Figma 15:3580~15:3615 / 15:3647~15:3674
const MessageCard = ({ card, isLong }) => (
  <article className="flex flex-col gap-2">
    <div className={`flex items-center ${card.pill ? 'gap-2' : 'gap-3'} text-14 font-semibold tracking-[-0.28px]`}>
      <span className={`shrink-0 whitespace-nowrap ${card.color} ${isLong ? `rounded-2xl px-2 ${card.pill ? 'border border-[#f04351] bg-[#fdecee] py-1' : 'py-0.5'}` : ''}`}>{card.label}</span>
      <span className="min-w-0 flex-1 text-[#52525b]">{card.condition}</span>
    </div>
    <div className="flex flex-col gap-3 rounded-lg bg-white p-3">
      <div className="flex flex-col gap-1.5">
        <p className="text-12 font-semibold text-[#a1a1aa]">발송 메시지</p>
        <p className="text-14 tracking-[-0.28px] text-[#18181b]">{card.message}</p>
      </div>
      {card.meta && <p className={`text-12 ${card.metaClass || (isLong ? 'text-[#71717a]' : 'font-medium text-[#71717a]')}`}>{card.meta}</p>}
    </div>
  </article>
);

const MessagePolicyModal = ({ open, onClose }) => {
  const [tab, setTab] = useState('long');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;
  const cards = tab === 'long' ? LONG_CARDS : SHORT_CARDS;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-5" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-[560px] flex-col overflow-hidden rounded-[10px] border border-[#e4e4e7] bg-[#f4f4f5] shadow-[0px_20px_48px_0px_rgba(0,0,0,0.18)]" onClick={(e) => e.stopPropagation()}>
        <header className="flex shrink-0 items-center justify-between gap-2 border-b border-[#e4e4e7] bg-white px-6 py-4">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" className="shrink-0">
              <path d="M15 2.5C15.8841 2.5 16.7319 2.85119 17.357 3.47631C17.9821 4.10143 18.3333 4.94928 18.3333 5.83333V12.5C18.3333 13.3841 17.9821 14.2319 17.357 14.857C16.7319 15.4821 15.8841 15.8333 15 15.8333H11.0633L7.095 18.2142C6.97549 18.2859 6.8399 18.3265 6.70064 18.3322C6.56139 18.338 6.42291 18.3088 6.29788 18.2472C6.17286 18.1856 6.06528 18.0936 5.98499 17.9796C5.90471 17.8657 5.85428 17.7335 5.83833 17.595L5.83333 17.5V15.8333H5C4.1448 15.8333 3.32231 15.5046 2.70266 14.9152C2.083 14.3258 1.71359 13.5208 1.67083 12.6667L1.66667 12.5V5.83333C1.66667 4.94928 2.01786 4.10143 2.64298 3.47631C3.2681 2.85119 4.11594 2.5 5 2.5H15ZM11.6667 10H6.66667C6.20643 10 5.83333 10.3731 5.83333 10.8333C5.83333 11.2936 6.20643 11.6667 6.66667 11.6667H11.6667C12.1269 11.6667 12.5 11.2936 12.5 10.8333C12.5 10.3731 12.1269 10 11.6667 10ZM13.3333 6.66667H6.66667C6.20643 6.66667 5.83333 7.03976 5.83333 7.5C5.83333 7.96024 6.20643 8.33333 6.66667 8.33333H13.3333C13.7936 8.33333 14.1667 7.96024 14.1667 7.5C14.1667 7.03976 13.7936 6.66667 13.3333 6.66667Z" fill="#BCBCC2" />
            </svg>
            <h2 className="flex-1 text-16 font-semibold tracking-[-0.32px] text-[#18181b]">메시지 발송 기준</h2>
          </div>
          <button type="button" onClick={onClose} className="flex h-6 w-6 shrink-0 items-center justify-center" aria-label="닫기"><X size={16} className="text-[#3f3f46]" strokeWidth={1.33} /></button>
        </header>

        <div className="flex shrink-0 items-end gap-6 px-6 pt-3">
          {[['long', '장기처방환자'], ['short', '단기처방환자']].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)} className={`border-b-2 py-1.5 text-16 font-semibold tracking-[-0.32px] ${tab === id ? 'border-[#1d356d] text-[#1d356d]' : 'border-transparent text-[#a1a1aa]'}`}>{label}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-6 p-6">{cards.map((card) => <MessageCard key={card.label} card={card} isLong={tab === 'long'} />)}</div>
          <div className="flex flex-col gap-2 border-t border-[#e4e4e7] px-6 py-4 text-12 ">
            <h3 className="font-semibold text-[#3f3f46]">발송 정책</h3>
            <ul className="flex flex-col gap-1 font-medium text-[#71717a]">{POLICY_ITEMS.map((item) => <li key={item}>· {item}</li>)}</ul>
          </div>
        </div>

        <footer className="flex shrink-0 items-start justify-end gap-2 border-t border-[#e4e4e7] bg-white px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-lg bg-[#1d356d] px-4 py-2 text-14 font-semibold tracking-[-0.28px] text-white">닫기</button>
        </footer>
      </div>
    </div>
  );
};

export default MessagePolicyModal;
